import type {
  CloverGiftCardActivity,
  CloverSalesReport,
} from '@/services/cloverReports'

export type GiftCardClassificationValue = 'exchange' | 'donation' | 'fulfilment'

export const GIFT_CARD_CLASSIFICATION_OPTIONS: ReadonlyArray<{
  value: GiftCardClassificationValue
  label: string
}> = [
  { value: 'exchange', label: 'Exchange' },
  { value: 'donation', label: 'Donation' },
  { value: 'fulfilment', label: 'Fulfilment of prior sale' },
]

const CLASSIFICATION_LABEL_BY_VALUE: Record<string, string> = {
  exchange: 'Exchange',
  donation: 'Donation',
  fulfilment: 'Fulfilment of prior sale',
}

const CLASSIFICATION_VALUE_BY_LABEL: Record<string, GiftCardClassificationValue> = {
  Exchange: 'exchange',
  Donation: 'donation',
  'Fulfilment of prior sale': 'fulfilment',
}

export type GiftCardDiffRow = {
  key: 'activations' | 'redemptions' | 'adjustments'
  label: string
  processor: number
  sales: number
  diff: number
}

function toNumber(value: number | string | null | undefined): number {
  if (value == null || value === '') return 0
  const num = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(num) ? num : 0
}

export function giftCardTenderAmount(salesReport: CloverSalesReport): number {
  const tender = salesReport.tender_types.find((row) => row.name === 'Gift Cards')
  return tender ? toNumber(tender.amount) : 0
}

export function classificationValueToLabel(value: string): string {
  return CLASSIFICATION_LABEL_BY_VALUE[value] ?? value
}

export function classificationLabelToValue(label: string): GiftCardClassificationValue | null {
  return CLASSIFICATION_VALUE_BY_LABEL[label] ?? null
}

export function mapProcessorSource(source: string): 'POS' | 'Virtual' {
  return source.toUpperCase().includes('PINLESS INTERNET') ? 'Virtual' : 'POS'
}

export function truncateEvidenceNote(
  note: string | null | undefined,
  maxLen = 60,
): string {
  if (!note?.trim()) return ''
  const trimmed = note.trim()
  if (trimmed.length <= maxLen) return trimmed
  return `${trimmed.slice(0, maxLen - 1)}…`
}

export function effectiveClassification(
  overrideKey: string,
  rowClassification: string,
  classifications: Record<string, string>,
): string {
  return classifications[overrideKey] ?? rowClassification
}

export function isMeaningfulGiftCardDiff(diff: number): boolean {
  return Math.abs(diff) >= 0.005
}

export function computeGiftCardDiffRows(
  activity: CloverGiftCardActivity | null | undefined,
  salesReport: CloverSalesReport,
): GiftCardDiffRow[] {
  const activationsProcessor =
    toNumber(activity?.total_activations) + toNumber(activity?.total_reloads)
  const activationsSales = toNumber(salesReport.gift_card_activations)

  const redemptionsProcessor = Math.abs(
    toNumber(activity?.total_redemptions) + toNumber(activity?.total_voids),
  )
  const redemptionsSales = Math.abs(giftCardTenderAmount(salesReport))

  const adjustmentsProcessor =
    toNumber(activity?.total_adjustments) + toNumber(activity?.total_service_fees)

  return [
    {
      key: 'activations',
      label: 'Activations + reloads',
      processor: activationsProcessor,
      sales: activationsSales,
      diff: activationsProcessor - activationsSales,
    },
    {
      key: 'redemptions',
      label: 'Redemptions (+ voids)',
      processor: redemptionsProcessor,
      sales: redemptionsSales,
      diff: redemptionsProcessor - redemptionsSales,
    },
    {
      key: 'adjustments',
      label: 'Adjustments / fees',
      processor: adjustmentsProcessor,
      sales: 0,
      diff: adjustmentsProcessor,
    },
  ]
}

export function hasProcessorReport(
  activity: CloverGiftCardActivity | null | undefined,
): boolean {
  return activity?.report_available === true
}
